import {
  AccountGroupCategory,
  useUpdateAccountGroupMutation,
} from '@/store/accountsApi';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Tab,
  Tabs,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { FC, useEffect, useState } from 'react';

interface EditAccountGroupFormProps {
  onCancel: () => void;
  accountGroup: AccountGroupCategory;
  onSetDefaultsClick?: () => void;
}

export const EditAccountGroupForm: FC<EditAccountGroupFormProps> = ({
  onCancel,
  accountGroup,
  onSetDefaultsClick,
}) => {
  const [innerTab, setInnerTab] = useState(0);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [options, setOptions] = useState<{ name: string; id?: number }[]>([]);
  const [updateAccountGroup, { isLoading: isUpdating }] =
    useUpdateAccountGroupMutation();
  const [isConfirmDeleteDialogOpen, setIsConfirmDeleteDialogOpen] =
    useState(false);
  const [segmentToDelete, setSegmentToDelete] = useState<number | null>(null);
  const theme = useTheme();
  useEffect(() => {
    setName(accountGroup.Name);
    setDescription(accountGroup.Description || '');
    setOptions(
      accountGroup.Tags.map((tag) => ({
        name: tag.Name,
        id: tag.InvestorTagId,
      })),
    );
  }, [accountGroup]);

  const handleInnerTabChange = (
    _event: React.SyntheticEvent,
    newValue: number,
  ) => {
    setInnerTab(newValue);
  };

  const handleOptionChange = (index: number, value: string) => {
    const newOptions = [...options];
    newOptions[index] = { ...newOptions[index], name: value };
    setOptions(newOptions);
  };

  const handleRemoveOption = (index: number) => {
    setSegmentToDelete(index);
    setIsConfirmDeleteDialogOpen(true);
  };

  const handleConfirmDelete = () => {
    if (segmentToDelete !== null) {
      const newOptions = options.filter((_, i) => i !== segmentToDelete);
      setOptions(newOptions);
    }
    setIsConfirmDeleteDialogOpen(false);
    setSegmentToDelete(null);
  };

  const handleCancelDelete = () => {
    setIsConfirmDeleteDialogOpen(false);
    setSegmentToDelete(null);
  };

  const handleAddRows = () => {
    setOptions([...options, { name: '' }]);
  };

  const handleSave = async () => {
    const updatedGroup = {
      ...accountGroup,
      Name: name,
      Description: description,
      Tags: options
        .map((opt) => ({ Name: opt.name, InvestorTagId: opt.id }))
        .filter((opt) => opt.Name),
    };

    try {
      await updateAccountGroup(updatedGroup).unwrap();
      onCancel(); // Close form on save
    } catch (error) {
      console.error('Failed to update account group', error);
    }
  };

  // Check if save should be disabled
  const validSegments = options.filter((opt) => opt.name.trim()).length;
  const isSaveDisabled = isUpdating || (innerTab === 1 && validSegments < 2);

  return (
    <Box>
      {/* Tabs and Assign Segments button */}
      <Box
        sx={(theme) => ({
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2,
          mt: 3,
          [theme.breakpoints.down(800)]: {
            display: 'block',
          },
        })}
      >
        <Tabs
          value={innerTab}
          onChange={handleInnerTabChange}
          aria-label='group-options-tabs'
          sx={(theme) => ({
            '& .MuiTabs-indicator': {
              display: 'none', // Hide the default indicator
            },
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '14px',
              color: 'text.secondary', // Gray color for unselected tabs
              backgroundColor: 'transparent',
              border: 'none',
              borderRadius: '8px',
              marginRight: '8px',
              minHeight: '40px',
              padding: '8px 16px',
              '&.Mui-selected': {
                color: 'primary.main', // Primary color for selected tab
                backgroundColor: 'rgba(25, 118, 210, 0.08)', // Light primary background
                fontWeight: 600,
              },
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
              },
              [theme.breakpoints.down(800)]: {
                display: 'block',
              },
            },
          })}
        >
          <Tab label='Group Details' />
          <Tab label='Group Segments' />
        </Tabs>
        <Button
          variant='outlined'
          size='large'
          onClick={onSetDefaultsClick}
          sx={(theme) => ({
            [theme.breakpoints.down('sm')]: {
              display: 'block',
              width: '100%',
              marginTop: 4,
              marginBottom: 4,
            },
          })}
        >
          Assign Segments per Account
        </Button>
      </Box>

      {/* Tab content */}
      <Box sx={{ pt: 0 }}>
        {innerTab === 0 && (
          <Box
            component='form'
            noValidate
            autoComplete='off'
          >
            <TextField
              required
              fullWidth
              id='name'
              label='Title'
              value={name}
              onChange={(e) => setName(e.target.value)}
              sx={{ mb: 3 }}
              variant='outlined'
            />

            <TextField
              fullWidth
              id='description'
              label='Description (optional)'
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              multiline
              rows={4}
              variant='outlined'
            />
          </Box>
        )}
        {innerTab === 1 && (
          <Box>
            <>
              <Typography
                variant='body1'
                color='text.primary'
                sx={{ mb: 3 }}
              >
                Segments define how you want to organize accounts within this
                group. Each account can be assigned to only one segment.
              </Typography>

              {options.map((option, index) => (
                <Box
                  key={option.id || index}
                  sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1 }}
                >
                  <TextField
                    variant='outlined'
                    placeholder='Segment name'
                    value={option.name}
                    onChange={(e) => handleOptionChange(index, e.target.value)}
                    sx={{
                      width: '300px',
                      '& .MuiOutlinedInput-root': {
                        fontSize: '14px',
                        '& fieldset': {
                          borderColor: '#d1d5db',
                        },
                        '&:hover fieldset': {
                          borderColor: '#9ca3af',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#2563eb',
                        },
                      },
                    }}
                  />
                  <IconButton
                    onClick={() => handleRemoveOption(index)}
                    aria-label='remove option'
                    sx={{
                      color: '#6b7280',
                      '&:hover': {
                        backgroundColor: 'rgba(0, 0, 0, 0.04)',
                        color: '#374151',
                      },
                    }}
                  >
                    <DeleteOutlineIcon fontSize='small' />
                  </IconButton>
                </Box>
              ))}

              <Button
                size='large'
                onClick={handleAddRows}
                sx={{
                  my: 2,
                  textTransform: 'none',
                  backgroundColor: 'transparent',
                  '&:hover': {
                    color: theme.palette.primary.dark,
                  },
                }}
              >
                + Add another segment
              </Button>
            </>
          </Box>
        )}
      </Box>

      {/* Bottom action buttons */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 4 }}>
        <Button
          size='large'
          onClick={onCancel}
          variant='outlined'
        >
          Cancel
        </Button>
        <Button
          size='large'
          variant='contained'
          onClick={handleSave}
          disabled={isSaveDisabled}
        >
          Save
        </Button>
      </Box>

      {/* Confirmation Dialog */}
      <Dialog
        open={isConfirmDeleteDialogOpen}
        onClose={handleCancelDelete}
        aria-labelledby='delete-confirm-dialog-title'
        aria-describedby='delete-confirm-dialog-description'
      >
        <DialogTitle id='delete-confirm-dialog-title'>
          Confirm Deletion
        </DialogTitle>
        <DialogContent>
          <DialogContentText id='delete-confirm-dialog-description'>
            Are you sure you want to delete this segment?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            size='large'
            onClick={handleCancelDelete}
          >
            Cancel
          </Button>
          <Button
            size='large'
            onClick={handleConfirmDelete}
            color='error'
            autoFocus
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EditAccountGroupForm;
